import React, { useEffect, useRef } from 'react'
import { BrowserRouter as Router, Routes, Route, Outlet, Navigate, useLocation, useParams } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import Layout from './components/Layout'
import AppUpdateBanner from './components/AppUpdateBanner'
import { useAppUpdateStore } from './services/appUpdateService'
import ScrollToTop from './components/ScrollToTop'
import Home from './pages/Home'
import Games from './pages/Games'
import Cases from './pages/Cases'
import CaseDetail from './pages/CaseDetail'
import CaseOpening from './pages/CaseOpening'
import CaseOpeningMulti from './pages/CaseOpeningMulti'
import Inventory from './pages/Inventory'
import Settings from './pages/Settings'
import { Payment } from './features/payment'
import PromoCode from './pages/PromoCode'
import TransactionHistory from './pages/TransactionHistory'
import Onboarding from './pages/Onboarding'
import { UpgraderNew } from './features/upgrader'
import { ContractNew } from './features/contract'
import Referrals from './pages/Referrals'
import ReferralTop from './pages/ReferralTop'
import Market from './pages/Market'
import Playground from './pages/Playground'
import Welcome from './pages/Welcome'
import Install from './pages/Install'
import { Battles, BattleCreate, BattlePage, BattleHistory } from './features/battle'
import { Royale, RoyaleHistory, RoyaleRound } from './features/royale'
import { Double, DoubleHistory, DoubleRound } from './features/double'
import { Wheel500, Wheel500History, Wheel500Round } from './features/wheel500'
import { Crash, CrashHistory, CrashRound } from './features/crash'
import { Slot, SlotHistory } from './features/slot'
import Mines from './features/mines/Mines'
import MinesHistory from './features/mines/MinesHistory'
import { HalloweenEventPage, HalloweenTeamPage } from './features/event'
import { Withdraw } from './features/withdraw'
import { RushDrop, RushDropWeeklyDraw } from './features/rushdrop'
import { Achievements, PublicProfile } from './features/profile'
import { Teams, TeamPage, TeamSettings, TeamTreasury, TeamTop, TeamCapture, TeamCaptureMode, TeamAudit, TeamStats, TeamGames, TeamGameDetail } from './features/team'
import OnboardingRouter from './components/OnboardingRouter'
import DeepLinkHandler from './components/DeepLinkHandler'
import DevTools from './components/DevTools'
import Loader from './components/Loader'
import { OnboardingPlusAward } from './components/OnboardingPlus'
import useMinimumLoadingTime from './hooks/useMinimumLoadingTime'
import useAppStore from './store/useAppStore'
import audioService from './services/audioService'
import { loginSupport } from './services/supportService'
import { sendYandexUserId, resetYandexUserIdSync } from './services/yandexMetrikaService'
import { trackPWAInstall, trackPWALaunch } from './services/trackingService'
import { syncPushSubscription } from './services/pushService'
import './i18n/index.js'
import { RTL_LANGUAGES } from './i18n/config'
import apiService from './services/api'
import { HALLOWEEN_THEME } from './config/brand'
import HalloweenTheme from './features/halloween/HalloweenTheme'

const WelcomeRoute = () => {
  const { isAuthorized, isLoading } = useAppStore(state => ({
    isAuthorized: state.isAuthorized,
    isLoading: state.isLoading
  }))
  // Прелоадер показываем минимум 2 секунды, даже если приложение загрузилось быстрее
  const showLoader = useMinimumLoadingTime(isLoading || isAuthorized === null)

  if (showLoader) {
    return <Loader message="" />
  }

  if (isAuthorized) {
    return <Navigate to="/" replace />
  }

  return <Welcome />
}

const ProtectedRoute = () => {
  const { isAuthorized, isLoading } = useAppStore(state => ({
    isAuthorized: state.isAuthorized,
    isLoading: state.isLoading
  }))
  // Прелоадер показываем минимум 2 секунды, даже если приложение загрузилось быстрее
  const showLoader = useMinimumLoadingTime(isLoading || isAuthorized === null)

  if (showLoader) {
    return <Loader message="" />
  }

  return <Outlet />
}

const OnboardingWrapper = () => {
  const isGuest = useAppStore((state) => state.isGuest)
  return isGuest ? <Outlet /> : (
    <OnboardingRouter>
      <Outlet />
    </OnboardingRouter>
  )
}

const GuestPlaceholder = () => {
  const { t } = useTranslation()
  const openLogin = useAppStore((state) => state.openLogin)
  return (
    <div className="mx-auto flex min-h-[60vh] max-w-lg flex-col items-center justify-center gap-5 px-6 text-center">
      <h1 className="text-2xl font-bold text-white">{t('guest.privateTitle')}</h1>
      <p className="text-text-secondary">{t('guest.privateDescription')}</p>
      <button type="button" onClick={openLogin} className="rounded-xl bg-accent-primary px-6 py-3 font-bold text-dark-primary">
        {t('guest.login')}
      </button>
    </div>
  )
}

const PrivatePage = ({ children }) => {
  const { pathname } = useLocation()
  const { isGuest, isAuthorized, isLoading } = useAppStore((state) => ({
    isGuest: state.isGuest,
    isAuthorized: state.isAuthorized,
    isLoading: state.isLoading
  }))
  useEffect(() => {
    if (isGuest && pathname.startsWith('/promo/')) {
      window.sessionStorage.setItem('skinoz_pending_promo', decodeURIComponent(pathname.slice(7)))
    }
  }, [isGuest, pathname])
  if (isLoading || isAuthorized === null) return <Loader message="" />
  return isGuest ? <GuestPlaceholder /> : children
}

const TeamPageAccess = ({ children }) => {
  const isGuest = useAppStore((state) => state.isGuest)
  const { slug } = useParams()
  return isGuest && slug === 'my' ? <GuestPlaceholder /> : children
}

const LoginModal = () => {
  const { t } = useTranslation()
  const isOpen = useAppStore((state) => state.isLoginOpen)
  const closeLogin = useAppStore((state) => state.closeLogin)
  const firstLink = useRef(null)
  const dialogRef = useRef(null)

  useEffect(() => {
    if (!isOpen) return undefined
    const previousFocus = document.activeElement
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    firstLink.current?.focus()
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') closeLogin()
      if (event.key !== 'Tab') return
      const focusables = dialogRef.current?.querySelectorAll('a[href], button:not([disabled])')
      if (!focusables?.length) return
      const first = focusables[0]
      const last = focusables[focusables.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = previousOverflow
      previousFocus?.focus?.()
    }
  }, [isOpen, closeLogin])

  if (!isOpen) return null
  const botUrl = new URL(`https://t.me/${import.meta.env.VITE_TELEGRAM_BOT || 'skinozbot'}`)
  const ref = apiService.captureAuthRefFromUrl()
  if (ref) botUrl.searchParams.set('start', ref)
  const providers = [
    { name: 'Google', url: apiService.getAuthLoginUrl('google') },
    { name: 'Steam', url: apiService.getAuthLoginUrl('steam') },
    { name: 'Discord', url: apiService.getAuthLoginUrl('discord') },
    { name: 'Telegram', url: apiService.getAuthLoginUrl('telegram') }
  ]

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center overflow-y-auto bg-black/85 p-3 sm:p-6" onMouseDown={(event) => { if (event.target === event.currentTarget) closeLogin() }}>
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="login-modal-title" aria-describedby="login-modal-description" className="relative my-auto grid w-full max-w-[800px] overflow-hidden rounded-3xl border border-white/10 bg-[#101216] shadow-2xl sm:grid-cols-2">
        <div className="relative h-56 overflow-hidden sm:h-auto sm:min-h-[380px]">
          <img src="/images/raccoon-login-form.jpg" alt="" className="block h-full w-full object-cover object-[center_25%]" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#101216] to-transparent sm:bg-gradient-to-r sm:from-transparent sm:to-[#101216] rtl:sm:bg-gradient-to-l" />
        </div>
        <div className="flex flex-col justify-center p-5 sm:p-7">
          <h2 id="login-modal-title" className="text-2xl font-black not-italic normal-case leading-tight text-white sm:text-3xl">{t('guest.loginModalTitle')}</h2>
          <p id="login-modal-description" className="mt-2 text-sm leading-relaxed text-text-secondary">{t('guest.loginModalDescription')}</p>
          <div className="mt-6 grid grid-cols-2 gap-2.5">
            {providers.map(({ name, url }, index) => (
              <a key={name} href={url} aria-label={t('welcome.loginWith', { provider: name })} className={`flex min-h-12 items-center justify-center gap-2 rounded-xl border px-3 py-2.5 text-sm font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-primary ${index === 0 ? 'border-accent-primary bg-accent-primary text-dark-primary hover:bg-accent-secondary' : 'border-white/10 bg-white/[0.06] text-white hover:border-accent-primary/50 hover:bg-accent-primary/10'}`}>
                <img src={`/images/welcome-page/${name.toLowerCase()}.png`} alt="" className="h-6 w-6 shrink-0 object-contain" />
                <span>{name}</span>
              </a>
            ))}
          </div>
          <a href={botUrl.toString()} target="_blank" rel="noopener noreferrer" className="mt-4 block text-center text-xs text-text-secondary underline underline-offset-4 transition-colors hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-primary">Telegram App</a>
        </div>
        <button type="button" ref={firstLink} onClick={closeLogin} aria-label={t('common.close')} className="absolute end-3 top-3 flex h-9 w-9 items-center justify-center rounded-xl border border-white/15 bg-black/60 text-xl leading-none text-white transition-colors hover:bg-black/80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-primary sm:bg-white/10">×</button>
      </div>
    </div>
  )
}

function App() {
  const { i18n } = useTranslation()
  const applyingUpdate = useAppUpdateStore((state) => state.applying)
  const { initializeApp, isAuthorized, isGuest, user, language, runtimeEnvironment } = useAppStore(state => ({
    initializeApp: state.initializeApp,
    isAuthorized: state.isAuthorized,
    isGuest: state.isGuest,
    user: state.user,
    language: state.language,
    runtimeEnvironment: state.runtimeEnvironment
  }))

  useEffect(() => {
    document.documentElement.lang = language
    document.documentElement.dir = RTL_LANGUAGES.includes(language) ? 'rtl' : 'ltr'
    if (i18n.language !== language) i18n.changeLanguage(language)
  }, [i18n, language])

  useEffect(() => {
    if (isAuthorized === null) {
      return
    }

    if (!isAuthorized) {
      resetYandexUserIdSync()
      return
    }

    if (user?.id) {
      sendYandexUserId(user.id)
    }
  }, [isAuthorized, user?.id])

  // Синхронизация состояния звука из профиля пользователя
  useEffect(() => {
    if (!isAuthorized || !user) {
      return
    }

    // me.sound по умолчанию true (бэкенд присылает bool)
    const soundEnabled = user.sound !== false
    audioService.setMuted(!soundEnabled)
  }, [isAuthorized, user?.sound])

  // Виджет поддержки только вне Telegram: входим заранее, чтобы чат открывался сразу.
  // Перезапуск только при смене игрока, а не на каждый ответ /api/me/
  const supportSubject = user?.support?.subject
  useEffect(() => {
    if (runtimeEnvironment !== 'browser' || !isAuthorized || !supportSubject) {
      return
    }

    loginSupport(useAppStore.getState().user?.support).catch(() => {})
  }, [runtimeEnvironment, isAuthorized, supportSubject])

  // Глобальные обработчики PWA-событий
  useEffect(() => {
    // Обработка установки PWA
    const handleAppInstalled = () => {
      console.log('✅ PWA установлено')
      trackPWAInstall()
    }

    window.addEventListener('appinstalled', handleAppInstalled)

    // Проверка первого запуска в standalone режиме
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches
      || window.navigator.standalone === true

    if (isStandalone) {
      const launchKey = 'skinoz_pwa_first_launch_tracked'
      if (!localStorage.getItem(launchKey)) {
        localStorage.setItem(launchKey, '1')
        trackPWALaunch()
        console.log('✅ PWA первый запуск отслежен')
      }
    }

    return () => {
      window.removeEventListener('appinstalled', handleAppInstalled)
    }
  }, [])

  // Синхронизация push-подписки с бэкендом: при старте приложения и после
  // авторизации (повторная отправка привязывает анонимную подписку к аккаунту).
  // Разрешение здесь не запрашивается — только обновление существующей подписки.
  useEffect(() => {
    if (!isAuthorized) {
      return
    }

    syncPushSubscription()
  }, [isAuthorized])

  // Запрос постоянного хранилища — предотвращает автоматическую очистку Cache Storage
  useEffect(() => {
    if (navigator.storage?.persist) {
      navigator.storage.persist().then((persisted) => {
        if (persisted) console.log('✅ Persistent storage активирован')
      }).catch(() => {})
    }
  }, [])

  useEffect(() => {
    // Предзагрузка изображений
    const preloadImage = (src, label) => {
      return new Promise(resolve => {
        const img = new Image()
        img.onload = () => {
          console.log(`✅ ${label} preloaded`)
          resolve()
        }
        img.onerror = () => {
          console.warn(`⚠️ Failed to preload ${label}`)
          resolve() // Не блокируем инициализацию при ошибке
        }
        img.src = src
      })
    }

    const preloadImages = () => {
      return Promise.all([
        preloadImage('/images/roulette-background.png', 'Roulette background'),
        preloadImage('/images/case-battle-bg.png', 'Case battle background'),
        preloadImage('/images/case-battle-winner.jpg', 'Case battle winner image')
      ])
    }

    // Инициализация приложения, предзагрузка звуков и изображений параллельно
    const initializeAll = async () => {
      try {
        // Запускаем все инициализации параллельно
        await Promise.all([
          initializeApp(i18n.changeLanguage),
          audioService.initialize(),
          preloadImages()
        ])
        console.log('✅ App, AudioService and Images initialized')
      } catch (error) {
        console.error('Failed to initialize app:', error)
      }
    }

    initializeAll()
  }, []) // Убираем initializeApp из зависимостей чтобы избежать повторных инициализаций

  return (
    <Router
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true
      }}
    >
      {/* Auto scroll to top on route changes */}
      <ScrollToTop />
      
      {/* Deep link handler for Telegram start_param */}
      <DeepLinkHandler>
        <div className="min-h-screen bg-dark-primary text-text-primary">
          {/* Animated Background */}
          <div className="animated-bg"></div>
          {HALLOWEEN_THEME && <HalloweenTheme />}
          
          {/* Main App Content */}
          <AppUpdateBanner />
          <div className="relative z-10" inert={applyingUpdate ? '' : undefined}>
            <Routes>
              {/* Welcome лендинг - публичный роут без Layout и без онбординг-редиректов */}
              <Route path="/welcome" element={<WelcomeRoute />} />

              {/* Install — публичная страница для трафика, установка PWA */}
              <Route path="/install" element={<Install />} />
              <Route element={<ProtectedRoute />}>
                <Route path="/profile/:slug/achievements" element={<Achievements />} />
                <Route element={<OnboardingWrapper />}>
                  {/* Онбординг - отдельный роут без Layout (без нижнего меню) */}
                  <Route path="/onboarding" element={<PrivatePage><Onboarding /></PrivatePage>} />
                  
                  {/* Основные страницы с Layout */}
                  <Route path="/" element={<Layout />}>
                    <Route index element={<Home />} />
                    <Route path="games" element={<Games />} />
                    <Route path="cases" element={<Cases />} />
                    <Route path="cases/:id" element={<CaseDetail />} />
                    <Route path="cases/:id/opening" element={<PrivatePage><CaseOpening /></PrivatePage>} />
                    <Route path="cases/:id/opening-multi" element={<PrivatePage><CaseOpeningMulti /></PrivatePage>} />
                    <Route path="inventory" element={<PrivatePage><Inventory /></PrivatePage>} />
                    {/* Таб «Профиль» — свой публичный профиль (GET /api/profile/); чужой — /profile/:slug */}
                    <Route path="profile" element={<PrivatePage><PublicProfile /></PrivatePage>} />
                    <Route path="profile/achievements" element={<PrivatePage><Achievements /></PrivatePage>} />
                    <Route path="profile/:slug" element={<PublicProfile />} />
                    {/* Настройки приложения — шестерёнка в шапке профиля */}
                    <Route path="settings" element={<PrivatePage><Settings /></PrivatePage>} />
                    <Route path="payment" element={<PrivatePage><Payment /></PrivatePage>} />
                    <Route path="promo/:code" element={<PrivatePage><PromoCode /></PrivatePage>} />
                    <Route path="transactions" element={<PrivatePage><TransactionHistory /></PrivatePage>} />
                    <Route path="upgrader" element={<UpgraderNew />} />
                    <Route path="upgrader-new" element={<Navigate to="/upgrader" replace />} />
                    <Route path="contract" element={<ContractNew />} />
                    <Route path="contract-new" element={<Navigate to="/contract" replace />} />
                    <Route path="referrals" element={<PrivatePage><Referrals /></PrivatePage>} />
                    <Route path="referrals/top" element={<ReferralTop key={isGuest ? 'guest' : 'user'} />} />
                    <Route path="market" element={<Market />} />
                    <Route path="playground" element={<Playground />} />
                    <Route path="event" element={<HalloweenEventPage key={isGuest ? 'guest' : 'user'} />} />
                    <Route path="event/team/:slug" element={<HalloweenTeamPage key={isGuest ? 'guest' : 'user'} />} />
                    <Route path="battles" element={<Battles key={isGuest ? 'guest' : 'user'} />} />
                    <Route path="battles/create" element={<PrivatePage><BattleCreate /></PrivatePage>} />
                    <Route path="battles/history" element={<BattleHistory key={isGuest ? 'guest' : 'user'} />} />
                    <Route path="battles/:id" element={<BattlePage key={isGuest ? 'guest' : 'user'} />} />
                    <Route path="royale" element={<Royale />} />
                    <Route path="royale/history" element={<RoyaleHistory key={isGuest ? 'guest' : 'user'} />} />
                    <Route path="royale/history/:id" element={<RoyaleRound key={isGuest ? 'guest' : 'user'} />} />
                    <Route path="double" element={<Double />} />
                    <Route path="double/history" element={<DoubleHistory key={isGuest ? 'guest' : 'user'} />} />
                    <Route path="double/history/:id" element={<DoubleRound key={isGuest ? 'guest' : 'user'} />} />
                    <Route path="x50" element={<Wheel500 />} />
                    <Route path="x50/history" element={<Wheel500History key={isGuest ? 'guest' : 'user'} />} />
                    <Route path="x50/history/:id" element={<Wheel500Round key={isGuest ? 'guest' : 'user'} />} />
                    <Route path="crash" element={<Crash />} />
                    <Route path="crash/history" element={<CrashHistory key={isGuest ? 'guest' : 'user'} />} />
                    <Route path="crash/history/:id" element={<CrashRound key={isGuest ? 'guest' : 'user'} />} />
                    <Route path="mines" element={<Mines key={isGuest ? 'guest' : 'user'} />} />
                    <Route path="mines/history" element={<PrivatePage><MinesHistory /></PrivatePage>} />
                    <Route path="mines/history/:uid" element={<PrivatePage><MinesHistory /></PrivatePage>} />
                    {/* Слоты: экран игры (гость может смотреть, крутить — нет) и личная история спинов */}
                    <Route path="slot" element={<Slot />} />
                    <Route path="slot/history" element={<PrivatePage><SlotHistory /></PrivatePage>} />
                    <Route path="slot/history/:id" element={<PrivatePage><SlotHistory /></PrivatePage>} />
                    <Route path="withdraw" element={<PrivatePage><Withdraw /></PrivatePage>} />
                    <Route path="rushdrop" element={<RushDrop />} />
                    <Route path="rushdrop/weekly" element={<RushDropWeeklyDraw />} />
                    {/* Банды: каталог, настройки/казна/история своей банды и карточка по slug
                        (статические сегменты объявлены до :slug, иначе они попадут в него) */}
                    <Route path="teams" element={<Teams />} />
                    <Route path="teams/settings" element={<PrivatePage><TeamSettings /></PrivatePage>} />
                    <Route path="teams/treasury" element={<PrivatePage><TeamTreasury /></PrivatePage>} />
                    <Route path="teams/top" element={<TeamTop />} />
                    <Route path="teams/capture" element={<TeamCapture />} />
                    <Route path="teams/capture/:mode" element={<TeamCaptureMode />} />
                    <Route path="teams/audit" element={<PrivatePage><TeamAudit /></PrivatePage>} />
                    <Route path="teams/:slug" element={<TeamPageAccess><TeamPage /></TeamPageAccess>} />
                    {/* Статистика командных игр и их история — по can_view_stats */}
                    <Route path="teams/:slug/stats" element={<TeamPageAccess><TeamStats /></TeamPageAccess>} />
                    <Route path="teams/:slug/games" element={<TeamPageAccess><TeamGames /></TeamPageAccess>} />
                    <Route path="teams/:slug/games/:id" element={<TeamPageAccess><TeamGameDetail /></TeamPageAccess>} />
                  </Route>
                </Route>
              </Route>
            </Routes>
          </div>
        </div>
      </DeepLinkHandler>
      
      <OnboardingPlusAward />
      <LoginModal />

      {/* Toast Notifications */}
      <Toaster
        position="top-center"
        toastOptions={{
          duration: 3000,
          style: {
            background: 'linear-gradient(135deg, var(--accent-primary) 0%, var(--accent-secondary) 100%)',
            color: '#0A0A0F',
            borderRadius: '12px',
            fontFamily: 'Poppins, sans-serif',
            fontWeight: '500',
          },
        }}
      />
      
      {/* Development Tools */}
      <DevTools />
    </Router>
  )
}

export default App
